import { useState } from 'react'
import { lumi } from '@/lib/lumi'

interface InvoiceItem {
  description: string
  quantity: number
  rate: number
}

interface Invoice {
  _id: string
  businessName: string
  clientName: string
  invoiceNumber: string
  invoiceDate: string
  items: InvoiceItem[]
  subtotalAmount: number
  taxRate: number
  taxAmount: number
  totalAmount: number
  currency: string
  status: 'draft' | 'sent' | 'paid' | 'pending_payment'
  userId?: string
  pdfUrl?: string
  logoUrl?: string
  stripeSessionId?: string
  stripePaymentIntentId?: string
  createdAt: string
  updatedAt: string
}

export const useInvoices = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchInvoices = async (options?: {
    filter?: Record<string, any>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
  }) => {
    setLoading(true)
    try {
      const { list, total } = await lumi.entities.invoices.list(options || {})
      setInvoices(list)
      setTotal(total)
      console.log('Invoices fetched:', list)
    } catch (error) {
      console.error('Fetch invoices failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const createInvoice = async (invoiceData: Omit<Invoice, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      const newInvoice = await lumi.entities.invoices.create({
        ...invoiceData,
        createdAt: now,
        updatedAt: now
      })
      console.log('Invoice created:', newInvoice)
      await fetchInvoices()
      return newInvoice
    } catch (error) {
      console.error('Create invoice failed:', error)
      throw error
    }
  }

  const updateInvoice = async (id: string, updates: Partial<Invoice>) => {
    try {
      if (typeof id !== 'string') {
        throw new Error('Invoice ID must be a string')
      }
      
      const updatedInvoice = await lumi.entities.invoices.update(id, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      console.log('Invoice updated:', updatedInvoice)
      await fetchInvoices()
      return updatedInvoice
    } catch (error) {
      console.error('Update invoice failed:', error)
      throw error
    }
  }

  const deleteInvoice = async (id: string) => {
    try {
      if (typeof id !== 'string' || id === '[object Object]') {
        throw new Error('Invalid invoice ID: must be a string, not an object')
      }
      
      await lumi.entities.invoices.delete(id)
      console.log('Invoice deleted:', id)
      await fetchInvoices()
    } catch (error) {
      console.error('Delete invoice failed:', error)
      throw error
    }
  }

  const bulkDeleteInvoices = async (ids: string[]) => {
    try {
      await Promise.all(ids.map(id => lumi.entities.invoices.delete(id)))
      console.log('Bulk deleted:', ids.length, 'invoices')
      await fetchInvoices()
    } catch (error) {
      console.error('Bulk delete failed:', error)
      throw error
    }
  }

  const bulkUpdateStatus = async (ids: string[], status: Invoice['status']) => {
    try {
      const now = new Date().toISOString()
      await Promise.all(
        ids.map(id => lumi.entities.invoices.update(id, { status, updatedAt: now }))
      )
      console.log('Bulk updated status:', ids.length, 'invoices to', status)
      await fetchInvoices()
    } catch (error) {
      console.error('Bulk status update failed:', error)
      throw error
    }
  }

  return {
    invoices,
    total,
    loading,
    fetchInvoices,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    bulkDeleteInvoices,
    bulkUpdateStatus
  }
}
